import {
  ExclamationTriangleIcon,
  EyeOpenIcon,
  QuestionMarkCircledIcon,
} from '@radix-ui/react-icons';
import { ClassValue } from 'clsx';
import { useTranslation } from 'react-i18next';
import {
  EventImageConsentEnum,
  toEventImageConsent,
} from '@clickersphere/core/enums/EventImageConsentEnum';
import cn from '../../../app/utils/cn';

interface IImageConsentNoticeProps {
  consent?: EventImageConsentEnum | null;
  hasLinkedEvent?: boolean;
  leadHasDeniedConsent?: boolean;
  className?: ClassValue;
}

export default function ImageConsentNotice({
  consent,
  hasLinkedEvent = true,
  leadHasDeniedConsent = false,
  className,
}: IImageConsentNoticeProps) {
  const { t } = useTranslation();
  const resolvedConsent = toEventImageConsent(consent);
  const isDenied =
    hasLinkedEvent && resolvedConsent === EventImageConsentEnum.DENIED;
  const isGranted =
    hasLinkedEvent && resolvedConsent === EventImageConsentEnum.GRANTED;
  const isLeadAtRisk = !isDenied && !isGranted && leadHasDeniedConsent;

  const content = (() => {
    if (isDenied) {
      return {
        Icon: ExclamationTriangleIcon,
        title: t('event.imageConsent.notice.deniedTitle'),
        description: t('event.imageConsent.notice.deniedDescription'),
      };
    }

    if (isGranted) {
      return {
        Icon: EyeOpenIcon,
        title: t('event.imageConsent.notice.grantedTitle'),
        description: undefined,
      };
    }

    if (isLeadAtRisk) {
      return {
        Icon: ExclamationTriangleIcon,
        title: t('event.imageConsent.notice.leadDeniedTitle'),
        description: t('event.imageConsent.notice.leadDeniedDescription'),
      };
    }

    if (!hasLinkedEvent) {
      return {
        Icon: QuestionMarkCircledIcon,
        title: t('event.imageConsent.notice.noEventTitle'),
        description: t('event.imageConsent.notice.noEventDescription'),
      };
    }

    return {
      Icon: QuestionMarkCircledIcon,
      title: t('event.imageConsent.notice.notAskedTitle'),
      description: t('event.imageConsent.notice.notAskedDescription'),
    };
  })();

  const { Icon, title, description } = content;

  return (
    <div
      role={isDenied || isLeadAtRisk ? 'alert' : 'note'}
      className={cn(
        'flex w-full items-start gap-2 rounded-lg p-3',
        isDenied &&
          'border-2 border-red-500 bg-red-50 text-red-800 dark:border-red-400 dark:bg-red-900/40 dark:text-red-100',
        isGranted &&
          'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200',
        isLeadAtRisk &&
          'border-2 border-amber-500 bg-amber-50 text-amber-900 dark:border-amber-400 dark:bg-amber-900/40 dark:text-amber-100',
        !isDenied &&
          !isGranted &&
          !isLeadAtRisk &&
          'bg-surface-input text-text-secondary border-border-default border',
        className,
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="flex min-w-0 flex-col gap-0.5">
        <span
          className={cn(
            'text-xs md:text-sm',
            isDenied && 'font-bold uppercase',
            isLeadAtRisk && 'font-semibold',
          )}
        >
          {title}
        </span>
        {description && (
          <span className="text-xs opacity-90 md:text-sm">{description}</span>
        )}
      </div>
    </div>
  );
}
